import { useMemo } from "react";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

interface UseBodyTemperatureChartConfigProps {
  selectedRange: string;
  navigationOffset: number;
  lowChartData: ChartDataPoint[];
  selectedDate?: Date;
}

interface ChartConfig {
  scale: { x: "time" | "linear" };
  domain: { x: [Date, Date] | [number, number]; y: [number, number] };
  tickValues: (Date | number)[];
  tickFormat: (x: Date | number) => string;
  yTickValues?: number[]; // Add Y-axis tick values
}

// Body temperature typically ranges from 95-105°F (35-40.6°C)
// Now using the actual scale after division by 100
const MIN_TEMPERATURE = 60.0; // 95.0°F
const MAX_TEMPERATURE = 200.0; // 105.0°F

export const useBodyTemperatureChartConfig = ({
  selectedRange,
  navigationOffset,
  lowChartData,
  selectedDate,
}: UseBodyTemperatureChartConfigProps): ChartConfig => {
  const chartConfig = useMemo(() => {
    // Calculate dynamic Y domain based on actual body temperature data
    let yDomain: [number, number] = [MIN_TEMPERATURE, MAX_TEMPERATURE]; // Default range
    let yTickValues: number[] = [60, 80, 100, 120, 140, 160, 180, 200]; // Default tick values

    if (lowChartData.length > 0) {
      const allYValues = lowChartData.map((point) => point.y);
      const minY = Math.min(...allYValues);
      const maxY = Math.max(...allYValues);

      // For body temperature, ensure reasonable range that adapts to data
      if (allYValues.length === 1) {
        // Single data point: create range around it
        const value = allYValues[0];
        const range = 20; // 20°F range around the value
        yDomain = [
          Math.max(50, Math.floor(value - range / 2)), // Minimum 50°F
          Math.min(250, Math.ceil(value + range / 2)), // Maximum 250°F
        ];
      } else {
        // Multiple data points: add more generous padding to ensure all data is visible
        const dataRange = maxY - minY;
        const yPadding = Math.max(10, dataRange * 0.25); // At least 10°F padding, or 25% of data range
        yDomain = [
          Math.max(40, Math.floor(minY - yPadding)), // Minimum 40°F (lowered from 50)
          Math.min(300, Math.ceil(maxY + yPadding)), // Maximum 300°F (increased from 250)
        ];
      }

      // Ensure Y-axis domain is consistent across all time ranges
      // Force minimum range of 20°F to prevent tiny ranges and ensure visibility
      const currentRange = yDomain[1] - yDomain[0];
      if (currentRange < 20) {
        const center = (yDomain[0] + yDomain[1]) / 2;
        yDomain = [
          Math.max(40, Math.floor(center - 10)),
          Math.min(300, Math.ceil(center + 10)),
        ];
      }

      // Generate reasonable tick values for body temperature
      const range = yDomain[1] - yDomain[0];
      const step = Math.max(2, Math.ceil(range / 8)); // At least 2°F between ticks, aim for 8+ ticks
      yTickValues = [];

      // Add safety check to prevent infinite loops
      const maxIterations = 30;
      let iterations = 0;

      for (
        let i = yDomain[0];
        i <= yDomain[1] && iterations < maxIterations;
        i += step
      ) {
        yTickValues.push(i);
        iterations++;
      }

      // Ensure we have at least the min and max values
      if (!yTickValues.includes(yDomain[0])) yTickValues.unshift(yDomain[0]);
      if (!yTickValues.includes(yDomain[1])) yTickValues.push(yDomain[1]);

      // If we still don't have enough values, add more intermediate ones
      if (yTickValues.length < 6) {
        const stepSize = Math.max(1, Math.floor(range / 6));
        yTickValues = [];
        for (let i = yDomain[0]; i <= yDomain[1]; i += stepSize) {
          yTickValues.push(i);
        }
        // Ensure we have the max value
        if (!yTickValues.includes(yDomain[1])) {
          yTickValues.push(yDomain[1]);
        }
      }

      // Sort tick values to ensure proper order
      yTickValues.sort((a, b) => a - b);
    }

    switch (selectedRange) {
      case "1d": {
        // Show every 2 hours: 12am, 2am, 4am, 6am, 8am, 10am, 12pm, 2pm, 4pm, 6pm, 8pm, 10pm
        // Use local timezone for day view

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
          yTickValues,
        };
      }
      case "7d": {
        // Show all 7 days of the week with local timezone and selected date
        const baseDate = selectedDate ?? new Date();
        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();
        const targetDate = baseDate.getDate();
        const targetDay = baseDate.getDay();

        // Calculate week start (Monday) and end (Sunday) in local timezone
        const weekStart = new Date(
          targetYear,
          targetMonth,
          targetDate - targetDay + 1,
        ); // Monday
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday
        weekEnd.setHours(0, 0, 0, 0);

        const weekTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i); // Mon to Sun
          // Set to noon to avoid timezone edge cases
          date.setHours(0, 0, 0, 0);
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [weekStart, weekEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: weekTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1m": {
        // Show enough days of the month with local timezone and selected date
        const baseDate = selectedDate ?? new Date();
        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();

        const monthStart = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0); // 1st of target month
        const monthEnd = new Date(targetYear, targetMonth + 1, 0, 0, 0, 0, 0); // Last day of target month

        // Create ticks for every 2 days
        const daysInMonth = monthEnd.getDate();
        const tickCount = Math.ceil(daysInMonth / 2);
        const monthTicks = Array.from({ length: tickCount }, (_, i) => {
          const day = Math.floor(i * 2) + 1;
          const date = new Date(targetYear, targetMonth, day, 0, 0, 0, 0);
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [monthStart, monthEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: monthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.getDate().toString();
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "6m": {
        // Show 6 months based on actual data from API or selected date
        const baseDate = selectedDate ?? new Date();
        let targetYear = baseDate.getFullYear();
        let isFirstHalf = true; // Default to Jan-Jun

        // Use data from API to determine the correct 6-month period
        if (lowChartData.length > 0) {
          // Get the year from the first data point
          const firstDataPoint = lowChartData[0];
          if (firstDataPoint.x instanceof Date) {
            targetYear = firstDataPoint.x.getFullYear();
            const firstMonth = firstDataPoint.x.getMonth();
            isFirstHalf = firstMonth <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)
          }
        } else {
          // Fallback to selected date logic
          const currentMonth = baseDate.getMonth();
          isFirstHalf = currentMonth <= 5;
        }

        const sixMonthTicks = Array.from({ length: 6 }, (_, i) => {
          const monthIndex = isFirstHalf ? i : i + 6; // 0-5 for Jan-Jun, 6-11 for Jul-Dec
          const date = new Date(targetYear, monthIndex, 1, 0, 0, 0); // 15th of each month at noon
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [
              new Date(targetYear, isFirstHalf ? 0 : 6, 1, 0, 0, 0), // Start of first month (Jan or Jul)
              new Date(targetYear, isFirstHalf ? 5 : 11, 1, 0, 0, 0), // End of last month (Jun or Dec)
            ] as [Date, Date],
            y: yDomain,
          },
          tickValues: sixMonthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1y": {
        // Show 12 months based on actual data from API or selected date
        const baseDate = selectedDate ?? new Date();
        let targetYear = baseDate.getFullYear();

        // Use data from API to determine the correct year
        if (lowChartData.length > 0) {
          // Get the year from the first data point
          const firstDataPoint = lowChartData[0];
          if (firstDataPoint.x instanceof Date) {
            targetYear = firstDataPoint.x.getFullYear();
          }
        }

        const yearTicks = Array.from({ length: 12 }, (_, i) => {
          const date = new Date(targetYear, i, 1, 0, 0, 0); // 1st of each month
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [
              new Date(targetYear, 0, 1, 0, 0, 0), // January 1st
              new Date(targetYear, 11, 31, 0, 0, 0), // December 31st
            ] as [Date, Date],
            y: yDomain,
          },
          tickValues: yearTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      default: {
        // Default to day view
        const baseDate = selectedDate ?? new Date();
        baseDate.setHours(0, 0, 0, 0);

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, navigationOffset, lowChartData, selectedDate]);

  return chartConfig;
};
